type DiagnosticValue = boolean | number | string | null | undefined

export type KycDiagnosticDetails = Record<string, DiagnosticValue>

export type KycDiagnostics = {
  error: (event: string, error: unknown, details?: KycDiagnosticDetails) => void
  event: (event: string, details?: KycDiagnosticDetails) => void
  id: string
  warn: (event: string, details?: KycDiagnosticDetails) => void
}

export type KycDiagnosticStage = {
  complete: (details?: KycDiagnosticDetails) => void
  fail: (error: unknown, details?: KycDiagnosticDetails) => void
}

const MAX_DIAGNOSTIC_STRING_LENGTH = 2_000
const DEFAULT_PENDING_WARNING_MS = 10_000
let diagnosticSequence = 0

/**
 * KYC telemetry is intentionally metadata-only. Never pass names, government
 * IDs, media URIs, presigned URLs, GraphQL variables, or media contents here.
 */
export function createKycDiagnostics(scope: string): KycDiagnostics {
  const id = `${Date.now().toString(36)}-${(++diagnosticSequence).toString(36)}`
  const startedAt = Date.now()

  const write = (
    level: 'error' | 'info' | 'warn',
    event: string,
    details: KycDiagnosticDetails = {},
    error?: unknown
  ) => {
    const payload = {
      scope,
      id,
      event,
      elapsedMs: Date.now() - startedAt,
      ...sanitizeDetails(details),
      ...(error === undefined ? {} : summarizeError(error))
    }

    if (level === 'error') {
      console.error('[KYC_DIAGNOSTICS]', payload)
    } else if (level === 'warn') {
      console.warn('[KYC_DIAGNOSTICS]', payload)
    } else {
      console.info('[KYC_DIAGNOSTICS]', payload)
    }
  }

  return {
    id,
    event: (event, details) => write('info', event, details),
    warn: (event, details) => write('warn', event, details),
    error: (event, error, details) => write('error', event, details, error)
  }
}

export function getUriDiagnosticMetadata(uri: string) {
  const schemeSeparator = uri.indexOf(':')
  const scheme = schemeSeparator === -1 ? 'unknown' : uri.slice(0, schemeSeparator).toLowerCase()
  const path = uri.split(/[?#]/, 1)[0]
  const lastSegment = path.split('/').at(-1) || ''
  const extension = lastSegment.includes('.') ? lastSegment.split('.').at(-1)?.toLowerCase() : undefined

  return { scheme, extension }
}

export function startKycDiagnosticStage(
  diagnostics: KycDiagnostics,
  stage: string,
  details: KycDiagnosticDetails = {},
  pendingWarningMs = DEFAULT_PENDING_WARNING_MS
): KycDiagnosticStage {
  const startedAt = Date.now()
  let settled = false
  diagnostics.event(`${stage}-started`, details)

  const pendingWarning = setTimeout(() => {
    if (settled) return
    diagnostics.warn(`${stage}-still-pending`, { pendingMs: Date.now() - startedAt })
  }, pendingWarningMs)

  const settle = () => {
    if (settled) return false
    settled = true
    clearTimeout(pendingWarning)
    return true
  }

  return {
    complete: (completionDetails = {}) => {
      if (!settle()) return
      diagnostics.event(`${stage}-completed`, {
        durationMs: Date.now() - startedAt,
        ...completionDetails
      })
    },
    fail: (error, failureDetails = {}) => {
      if (!settle()) return
      diagnostics.error(`${stage}-failed`, error, {
        durationMs: Date.now() - startedAt,
        ...failureDetails
      })
    }
  }
}

function sanitizeDetails(details: KycDiagnosticDetails) {
  return Object.fromEntries(
    Object.entries(details)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, typeof value === 'string' ? sanitizeString(value) : value])
  )
}

function summarizeError(error: unknown): KycDiagnosticDetails {
  if (error instanceof Error) {
    const errorWithCode = error as Error & { code?: unknown }
    return {
      errorName: sanitizeString(error.name),
      errorMessage: sanitizeString(error.message),
      errorCode: normalizeCode(errorWithCode.code),
      errorStack: error.stack ? sanitizeString(error.stack) : undefined
    }
  }

  if (typeof error === 'object' && error !== null) {
    const value = error as Record<string, unknown>
    return {
      errorName: normalizeString(value.name),
      errorMessage: normalizeString(value.message),
      errorCode: normalizeCode(value.code),
      errorStatus: normalizeCode(value.status)
    }
  }

  return { errorMessage: sanitizeString(String(error)) }
}

function normalizeString(value: unknown) {
  return typeof value === 'string' ? sanitizeString(value) : undefined
}

function normalizeCode(value: unknown) {
  return typeof value === 'number' || typeof value === 'string' ? sanitizeString(String(value)) : undefined
}

function sanitizeString(value: string) {
  return value
    .replace(/(?:https?|file|content):\/\/\S+/gi, '[URI_REDACTED]')
    .replace(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g, '[EMAIL_REDACTED]')
    .replace(/\b\d{6,}\b/g, '[NUMBER_REDACTED]')
    .slice(0, MAX_DIAGNOSTIC_STRING_LENGTH)
}
